import { useState } from 'react';
import { User } from '../types';
import { UserInviteStatus, UserStatus } from '../constants';
import { EllipsisVerticalIcon, Send, Pencil, CircleOff } from 'lucide-react';
import { Button } from '@predoc/cow';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import {
  DropdownMenuTrigger,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
} from '@predoc/cow';
import { DeactivateUserModal } from './DeactivateUserModal';
import { Permission, useHasPermission } from '@/utils/roles';
import { useUserReinvite } from '../hooks/usersApi';

export const ActionDropdown = ({ user }: { user: User }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { hasPermission: canEdit } = useHasPermission(Permission.EDIT_USERS);
  const { hasPermission: canDeactivate } = useHasPermission(
    Permission.DEACTIVATE_USERS,
  );
  const { hasPermission: canReinvite } = useHasPermission(
    Permission.REINVITE_USERS,
  );
  const { mutate: reinviteUser } = useUserReinvite();
  const navigate = useNavigate();

  if (user.canBeEdited === false) {
    return null;
  }

  if (user.status === UserStatus.INACTIVE) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="focus:border-none focus:ring-0 focus:outline-none focus-visible:border-none focus-visible:ring-0 focus-visible:outline-none"
        aria-label="User actions"
        disabled
      >
        <EllipsisVerticalIcon className="size-5" />
      </Button>
    );
  }

  const hadBeenInvited =
    (user.status === UserStatus.ACTIVE ||
      user.status === UserStatus.UNRESPONSIVE) &&
    user.invitationStatus !== UserInviteStatus.ACCEPTED &&
    user.invitationStatus !== undefined;

  const showResend = hadBeenInvited && canReinvite;
  const showEdit = canEdit;
  const showDeactivate =
    canDeactivate &&
    !user.isCurrentUser &&
    user.status !== UserStatus.UNRESPONSIVE &&
    user.invitationStatus !== UserInviteStatus.PENDING;

  if (!showResend && !showEdit && !showDeactivate) {
    return null;
  }

  const handleResendInvitation = () => {
    reinviteUser(
      { userId: user.id },
      {
        onSuccess: () => {
          toast.success(`Invitation sent to ${user.emailAddress}`);
        },
        onError: () => {
          toast.error(`Invitation was not sent to ${user.emailAddress}`);
        },
      },
    );
  };

  const handleEdit = () => {
    navigate(`/users/${user.id}`);
  };

  const handleOpenDialog = () => {
    setIsOpen(true);
    setIsDropdownOpen(false);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  return (
    <>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="focus:border-none focus:ring-0 focus:outline-none focus-visible:border-none focus-visible:ring-0 focus-visible:outline-none"
            aria-label="User actions"
            disabled={user.isCurrentUser}
          >
            <EllipsisVerticalIcon className="size-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="start">
          {showResend ? (
            <DropdownMenuItem onClick={handleResendInvitation}>
              <Send size="4" />
              Resend Invitation
            </DropdownMenuItem>
          ) : null}
          {showEdit ? (
            <DropdownMenuItem onClick={handleEdit}>
              <Pencil size="4" />
              Edit
            </DropdownMenuItem>
          ) : null}
          {showDeactivate ? (
            <>
              {(showResend || showEdit) && <DropdownMenuSeparator />}
              <DropdownMenuItem
                className="text-destructive"
                onClick={handleOpenDialog}
              >
                <CircleOff size="4" className="text-destructive" />
                Deactivate
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <DeactivateUserModal
        open={isOpen}
        handleClose={handleCloseModal}
        user={user}
      />
    </>
  );
};
